import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, Clock, PlayCircle, RotateCcw, Loader2, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function CourseDetail() {
  const { slug } = useParams();
  const { user, refreshUser } = useAuth();
  const [course, setCourse] = useState(null);
  const [completed, setCompleted] = useState(new Set());
  const [resetting, setResetting] = useState(false);

  const load = () => {
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => {});
    api.get("/progress").then((r) => setCompleted(new Set(r.data.completed_lessons))).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!course) return <div className="font-bold">Lädt...</div>;

  const doneCount = course.lessons.filter((l) => completed.has(`${slug}/${l.id}`)).length;
  const pct = Math.round((doneCount / course.lessons.length) * 100);

  const handleReset = async () => {
    if (!confirm(`Soll dein Fortschritt im Kurs "${course.title}" wirklich zurückgesetzt werden? Bereits verdiente XP aus diesem Kurs werden abgezogen.`)) {
      return;
    }
    setResetting(true);
    try {
      await api.post(`/courses/${slug}/reset`);
      toast.success("Kursfortschritt wurde zurückgesetzt.");
      load();
      await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="course-detail">
      <Link to="/kurse" className="inline-flex items-center gap-1 font-bold text-sm hover:underline" data-testid="back-to-courses">
        <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> Alle Kurse
      </Link>

      <div className="nb-card relative overflow-hidden p-6 sm:p-8" style={{ borderColor: course.color, boxShadow: `0 0 32px ${course.color}26` }}>
        <span className="absolute inset-x-0 top-0 h-0.5" style={{ backgroundColor: course.color, boxShadow: `0 0 12px ${course.color}` }} aria-hidden="true" />
        <span className="font-mono text-xs uppercase tracking-widest rounded-full border px-2.5 py-0.5" style={{ borderColor: course.color, color: course.color }}>{course.level}</span>
        <h1 className="font-heading font-bold text-4xl tracking-tight mt-3 text-foreground">{course.title}</h1>
        <p className="font-semibold text-foreground/90 mt-1">{course.subtitle}</p>
        <p className="text-muted-foreground mt-3 max-w-2xl">{course.description}</p>
        {user ? (
          <div className="mt-5 max-w-md">
            <div className="flex justify-between font-mono text-sm text-muted-foreground mb-1.5">
              <span>{doneCount}/{course.lessons.length} abgeschlossen</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-line overflow-hidden">
              <div className="h-full rounded-full trace-on transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        ) : (
          <div className="mt-5 max-w-md rounded-xl border border-primary/40 bg-primary/10 p-4 text-sm font-medium text-foreground">
            Melde dich an, um deinen Lernfortschritt zu speichern und Lektionen zu starten!
          </div>
        )}
        {doneCount > 0 && (
          <button
            onClick={handleReset}
            disabled={resetting}
            data-testid="course-reset-button"
            className="mt-4 flex items-center gap-2 text-xs font-bold bg-white border-2 border-black rounded-lg px-3 py-1.5 nb-shadow-sm nb-press disabled:opacity-60"
          >
            {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} /> : <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.5} />}
            Kurs zurücksetzen
          </button>
        )}
      </div>

      <div className="space-y-3">
        <h2 className="font-heading font-black text-2xl">Lektionen</h2>
        {course.lessons.map((lesson, i) => {
          const isDone = completed.has(`${slug}/${lesson.id}`);
          if (!user) {
            return (
              <div
                key={lesson.id}
                title="Melde dich an, um diese Lektion zu starten"
                className="nb-card p-4 flex items-center gap-4 opacity-70 bg-muted"
              >
                <div className="font-heading font-black text-xl w-8 text-center text-muted-foreground">{i + 1}</div>
                <Lock className="h-6 w-6 text-muted-foreground/80 shrink-0" strokeWidth={2.5} />
                <div className="flex-1">
                  <div className="font-bold text-muted-foreground">{lesson.title}</div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                    <Clock className="h-3.5 w-3.5" strokeWidth={2.5} /> {lesson.duration}
                  </div>
                </div>
                <Link
                  to="/register"
                  className="bg-primary text-white font-bold text-xs px-3.5 py-1.5 rounded-lg border-2 border-black nb-shadow-sm nb-press shrink-0"
                >
                  Freischalten
                </Link>
              </div>
            );
          }
          return (
            <Link
              key={lesson.id}
              to={`/kurs/${slug}/lektion/${lesson.id}`}
              data-testid={`lesson-item-${lesson.id}`}
              className="nb-card nb-press p-4 flex items-center gap-4"
            >
              <div className="font-heading font-black text-xl w-8 text-center text-muted-foreground">{i + 1}</div>
              {isDone ? (
                <CheckCircle2 className="h-7 w-7 text-success shrink-0" strokeWidth={2.5} />
              ) : (
                <Circle className="h-7 w-7 text-muted-foreground shrink-0" strokeWidth={2.5} />
              )}
              <div className="flex-1">
                <div className="font-bold">{lesson.title}</div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  <Clock className="h-3.5 w-3.5" strokeWidth={2.5} /> {lesson.duration}
                </div>
              </div>
              <PlayCircle className="h-6 w-6 text-primary" strokeWidth={2.5} />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
